import type { AuditEventRow, AuditListResponse } from "@lms/shared-types";
import { useCallback, useEffect, useId, useState } from "react";
import { fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, StatusPill, TextField, type Tone } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { useFormat } from "../../lib/format";

/**
 * The audit trail (REC-14, design.md §16 "Program › Audit trail"): the
 * rows the caller's scope reaches, newest first, with the filters the API
 * has, a "load more" cursor, and the export. States are words, never a
 * colour alone; the diff of a change is a definition list inside the
 * row's details; a withheld change says it was withheld. The sentence on
 * what this trail cannot show is the template's own advice.
 */
const OUTCOME_TONE: Record<AuditEventRow["outcome"], Tone> = { applied: "ok", refused: "danger", attempted: "warn", failed: "danger" };
const OUTCOME_WORD: Record<AuditEventRow["outcome"], string> = { applied: "Applied", refused: "Refused", attempted: "Attempted", failed: "Failed" };
const AUTHORITY_WORD = { not_required: "No approval needed", approved: "Approved", self_approved: "Self-approved", break_glass: "Break-glass" } as const;

function Values({ label, values }: { label: string; values: Record<string, unknown> }) {
  const entries = Object.entries(values);
  if (entries.length === 0) return null;
  return (
    <div>
      <p className="font-semibold">{label}</p>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr] [&>*]:min-w-0">
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted">{k}</dt>
            <dd className="break-words">{v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Change({ e }: { e: AuditEventRow }) {
  if (e.redacted) return <p className="text-muted">The change touched a sensitive record; its values are withheld here.</p>;
  if (!e.before && !e.after && !e.details && !e.reason) return <span className="text-muted">—</span>;
  return (
    <details>
      <summary className="cursor-pointer">Details</summary>
      <div className="mt-2 grid gap-3">
        {e.reason && (
          <p>
            <span className="font-semibold">Reason:</span> {e.reason}
          </p>
        )}
        {e.before && <Values label="Before" values={e.before} />}
        {e.after && <Values label="After" values={e.after} />}
        {e.details && <Values label="Details" values={e.details} />}
      </div>
    </details>
  );
}

export function AuditTrailPage() {
  const { session, config, me } = useAuth();
  const format = useFormat();
  const announce = useAnnounce();
  const formId = useId();
  const [filters, setFilters] = useState({ from: "", to: "", action: "", outcome: "" });
  const [applied, setApplied] = useState(filters);
  const [state, setState] = useState<{ events: AuditEventRow[]; nextCursor?: string; loading: boolean; error?: unknown }>({ events: [], loading: true });
  const [exporting, setExporting] = useState<{ busy: boolean; error?: unknown }>({ busy: false });
  const canExport = me?.permissions.includes("audit_event.export") ?? false;

  const query = useCallback(
    (cursor?: string) => {
      const q = new URLSearchParams();
      if (applied.from) q.set("from", new Date(applied.from).toISOString());
      if (applied.to) q.set("to", new Date(applied.to).toISOString());
      if (applied.action) q.set("action", applied.action);
      if (applied.outcome) q.set("outcome", applied.outcome);
      q.set("limit", "50");
      if (cursor) q.set("cursor", cursor);
      return `/v1/audit?${q.toString()}`;
    },
    [applied],
  );

  useEffect(() => {
    if (!session) return;
    let live = true;
    setState({ events: [], loading: true });
    fetchJson<AuditListResponse>(config, session, query())
      .then((r) => {
        if (!live) return;
        setState({ events: r.events, loading: false, ...(r.nextCursor ? { nextCursor: r.nextCursor } : {}) });
        announce(r.events.length === 0 ? "No audit events match." : `${r.events.length} audit events shown${r.nextCursor ? ", more available" : ""}.`);
      })
      .catch((error: unknown) => live && setState({ events: [], loading: false, error }));
    return () => {
      live = false;
    };
  }, [session, config, query, announce]);

  const loadMore = async () => {
    if (!session || !state.nextCursor) return;
    try {
      const r = await fetchJson<AuditListResponse>(config, session, query(state.nextCursor));
      setState((s) => ({ events: [...s.events, ...r.events], loading: false, ...(r.nextCursor ? { nextCursor: r.nextCursor } : {}) }));
      announce(`${r.events.length} more audit events loaded.`);
    } catch (error) {
      setState((s) => ({ ...s, error }));
    }
  };

  const exportCsv = async () => {
    if (!session) return;
    const from = applied.from ? new Date(applied.from) : new Date(Date.now() - 30 * 86_400_000);
    const to = applied.to ? new Date(applied.to) : new Date();
    setExporting({ busy: true });
    try {
      const path = `/v1/audit/export?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&format=csv`;
      const body = await fetchJson<unknown>(config, session, path);
      // The mock answers JSON; the real API answers a CSV attachment. Either way the file lands as a download.
      const text = typeof body === "string" ? body : JSON.stringify(body, null, 2);
      const blob = new Blob([text], { type: typeof body === "string" ? "text/csv" : "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit-${from.toISOString().slice(0, 10)}-${to.toISOString().slice(0, 10)}.${typeof body === "string" ? "csv" : "json"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      announce("Export downloaded. The export itself is now in the trail.");
      setExporting({ busy: false });
    } catch (error) {
      setExporting({ busy: false, error });
    }
  };

  const columns = [
    { key: "when", header: "When", render: (e: AuditEventRow) => <time dateTime={e.occurredAt}>{format.when(e.occurredAt)}</time>, sortValue: (e: AuditEventRow) => e.occurredAt },
    { key: "actor", header: "Who", render: (e: AuditEventRow) => (e.actor.name ? `${e.actor.name} (${e.actor.email ?? e.actor.subject})` : e.actor.subject), sortValue: (e: AuditEventRow) => e.actor.name ?? e.actor.subject },
    { key: "action", header: "Action", render: (e: AuditEventRow) => <code>{e.action}</code>, sortValue: (e: AuditEventRow) => e.action },
    { key: "resource", header: "Record", render: (e: AuditEventRow) => `${e.resource.kind} ${e.resource.id}` },
    { key: "outcome", header: "Outcome", render: (e: AuditEventRow) => <StatusPill tone={OUTCOME_TONE[e.outcome]}>{OUTCOME_WORD[e.outcome]}</StatusPill>, sortValue: (e: AuditEventRow) => e.outcome },
    { key: "authority", header: "Authority", render: (e: AuditEventRow) => AUTHORITY_WORD[e.authority] },
    { key: "change", header: "Change", render: (e: AuditEventRow) => <Change e={e} /> },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Program"
        title="Audit trail"
        subtitle="Every change of record and every refusal, newest first, within the programs you administer."
        actions={
          canExport ? (
            <Button onClick={() => void exportCsv()} disabled={exporting.busy}>
              {exporting.busy ? "Exporting…" : "Export"}
            </Button>
          ) : undefined
        }
      />
      <Card title="Filter">
        <form
          aria-labelledby={`${formId}-legend`}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 [&>*]:min-w-0"
          onSubmit={(ev) => {
            ev.preventDefault();
            setApplied(filters);
          }}
        >
          <span id={`${formId}-legend`} className="sr-only">
            Filter the audit trail
          </span>
          <TextField label="From" type="date" value={filters.from} onChange={(ev) => setFilters({ ...filters, from: ev.target.value })} />
          <TextField label="To" type="date" value={filters.to} onChange={(ev) => setFilters({ ...filters, to: ev.target.value })} />
          <TextField label="Action" placeholder="role.granted" value={filters.action} onChange={(ev) => setFilters({ ...filters, action: ev.target.value.trim() })} />
          <label className="grid gap-1">
            <span>Outcome</span>
            <select className="min-h-11 rounded-lg border border-line bg-surface px-3" value={filters.outcome} onChange={(ev) => setFilters({ ...filters, outcome: ev.target.value })}>
              <option value="">Any</option>
              <option value="applied">Applied</option>
              <option value="refused">Refused</option>
              <option value="attempted">Attempted</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <div className="flex items-end">
            <Button type="submit" variant="primary">
              Apply filters
            </Button>
          </div>
        </form>
      </Card>
      {exporting.error ? <ErrorState title="The export failed" error={exporting.error} onRetry={() => void exportCsv()} /> : null}
      <div className="mt-6">
        {state.loading ? (
          <LoadingState label="Loading the audit trail…" />
        ) : state.error ? (
          <ErrorState title="The audit trail could not be loaded" error={state.error} onRetry={() => setApplied({ ...applied })} />
        ) : (
          <>
            <DataTable columns={columns} rows={state.events} rowKey={(e) => e.id} caption="Audit events" empty={<EmptyState title="No audit events" hint="Nothing matches these filters, or nothing has been recorded yet." />} initialSort={{ key: "when", dir: "descending" }} rowTestId={(e) => `audit-${e.id}`} />
            {state.nextCursor && (
              <p className="mt-4">
                <Button onClick={() => void loadMore()}>Load more</Button>
              </p>
            )}
          </>
        )}
      </div>
      <p className="mt-6 text-sm text-muted">
        What this trail cannot show: changes made directly in the cloud account rather than through the product. Those are in the account's own CloudTrail, which the operator keeps. A read of a sensitive record appears here as its own event; the values of a change to one are withheld unless you hold the permission to read them.
      </p>
    </>
  );
}
